/**
 * TicketsByClientBar — barres empilées par statut pour chaque client
 */

import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TicketIcon } from 'lucide-react';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3 min-w-[180px]">
      <p className="font-semibold text-gray-800 text-sm mb-2 truncate">{label}</p>
      {payload.map((p, i) => p.value > 0 && (
        <div key={i} className="flex items-center justify-between gap-3 text-xs mb-0.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: p.fill }} />
            <span className="text-gray-600">{p.name}</span>
          </span>
          <span className="font-bold text-gray-800">{p.value}</span>
        </div>
      ))}
      <div className="border-t border-gray-100 mt-2 pt-1.5 flex justify-between text-xs">
        <span className="text-gray-500">Total</span>
        <span className="font-bold text-gray-800">{total}</span>
      </div>
    </div>
  );
};

const TicketsByProjectPie = ({ data = {}, colorMap = {} }) => {
  const { chartData = [], stages = [], total = 0 } = data;

  if (chartData.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col items-center justify-center min-h-[200px]">
        <TicketIcon className="w-10 h-10 text-gray-200 mb-3" />
        <p className="text-sm text-gray-400">Aucun ticket ouvert</p>
      </div>
    );
  }

  // Total par statut pour la légende
  const stageTotals = {};
  chartData.forEach(c => {
    stages.forEach(s => { stageTotals[s.name] = (stageTotals[s.name] || 0) + (c[s.name] || 0); });
  });

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
          <TicketIcon className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">Tickets à traiter par client</h3>
          <p className="text-xs text-gray-500">{total} ticket{total > 1 ? 's' : ''} au total</p>
        </div>
      </div>

      <div style={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 20, left: 0, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
            <XAxis
              dataKey="clientName"
              axisLine={false}
              tickLine={false}
              angle={-35}
              textAnchor="end"
              interval={0}
              tick={({ x, y, payload }) => (
                <text x={x} y={y + 8} transform={`rotate(-35, ${x}, ${y + 8})`}
                  textAnchor="end" fontSize={11}
                  fill={colorMap[payload.value] || '#374151'} fontWeight={600}>
                  {payload.value.length > 16 ? payload.value.slice(0, 16) + '…' : payload.value}
                </text>
              )}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#9CA3AF' }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="square"
              iconSize={8}
              verticalAlign="top"
              wrapperStyle={{ fontSize: 11, paddingBottom: 12 }}
              formatter={(value) => `${value} (${stageTotals[value] || 0})`}
            />
            {stages.map((stage, i) => (
              <Bar
                key={stage.name}
                dataKey={stage.name}
                stackId="a"
                fill={stage.color}
                radius={i === stages.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TicketsByProjectPie;
